import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Avatar,
  Typography,
  Button,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import BusinessIcon from "@mui/icons-material/Business";
import MessageIcon from "@mui/icons-material/Message";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { users as usersApi, posts as postsApi, companies as companiesApi, inquiries } from "../api/endpoints";
import type { User, Post, Company } from "../types";
import PostCard from "../components/PostCard";

const POST_TYPE_KEYS: Record<string, string> = {
  general_discussion: "feed.generalDiscussion",
  housing_offer:      "feed.housingOffer",
  housing_request:    "feed.housingRequest",
  job_offer:          "feed.jobOffer",
  job_request:        "feed.jobRequest",
  friend_request:     "feed.friendRequest",
  other:              "feed.other",
};

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}
function TabPanel({ children, value, index }: TabPanelProps) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function Profile() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user: currentUser, setUser } = useAuth();
  const { t } = useLanguage();

  const [profile, setProfile] = useState<User | null>(null);
  const [userPosts, setUserPosts] = useState<Post[]>([]);
  const [userCompanies, setUserCompanies] = useState<Company[]>([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", bio: "" });
  const [editLoading, setEditLoading] = useState(false);

  const [inquireOpen, setInquireOpen] = useState(false);
  const [inquireCompany, setInquireCompany] = useState<Company | null>(null);
  const [inquireMsg, setInquireMsg] = useState("");
  const [inquireLoading, setInquireLoading] = useState(false);
  const [inquireError, setInquireError] = useState<string | null>(null);

  const isOwn = currentUser?.username === username;

  // First resolve the profile so we have its user id, then fetch that user's
  // posts server-side. The backend only returns this user's anonymous posts
  // when the viewer is the user themselves, so no client-side filtering of
  // anonymous content is needed here.
  const loadProfile = useCallback(() => {
    if (!username) return;
    setLoading(true);
    setError(null);
    usersApi
      .byUsername(username)
      .then((u) =>
        Promise.all([
          Promise.resolve(u),
          postsApi.list({ creator_id: u.id, limit: 50 }),
          companiesApi.list({}),
        ]),
      )
      .then(([u, p, c]) => {
        setProfile(u);
        setUserPosts(p.data ?? []);
        setUserCompanies((c.data ?? []).filter((co) => co.owner?.username === username || co.owner_id === u.id));
        setEditForm({ first_name: u.first_name, last_name: u.last_name, bio: u.bio ?? "" });
      })
      .catch((err) => setError(err instanceof Error ? err.message : t("common.noResults")))
      .finally(() => setLoading(false));
  }, [username, t]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleInquire = async () => {
    if (!inquireCompany) return;
    setInquireLoading(true);
    setInquireError(null);
    try {
      await inquiries.create({
        receiver_id: inquireCompany.owner_id,
        entity_type: "company",
        entity_id: inquireCompany.id,
        message: inquireMsg.trim(),
      });
      setInquireOpen(false);
      setInquireMsg("");
    } catch (err) {
      setInquireError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setInquireLoading(false);
    }
  };

  const handleEditSave = async () => {
    setEditLoading(true);
    try {
      const updated = await usersApi.updateMe(editForm);
      setProfile(updated);
      setUser(updated);
      setEditOpen(false);
    } catch (err) {
      // ignore
    } finally {
      setEditLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error">{error ?? t("profile.userNotFound")}</Alert>
      </Container>
    );
  }

  return (
    <Box>
      {/* Cover + Avatar */}
      <Box
        sx={{
          background: "linear-gradient(145deg, #0F2156 0%, #1a3a7a 45%, rgba(185,28,44,0.85) 100%)",
          height: 140,
          position: "relative",
        }}
      />
      <Container maxWidth="sm">
        <Box sx={{ position: "relative", mt: "-40px", mb: 2 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              fontSize: "2rem",
              bgcolor: "white",
              color: "primary.main",
              border: "3px solid white",
              boxShadow: 2,
            }}
          >
            {profile.first_name.charAt(0).toUpperCase()}
          </Avatar>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {profile.first_name} {profile.last_name}
            </Typography>
            {isOwn && (
              <Typography variant="body2" color="text.secondary">
                @{profile.username}
              </Typography>
            )}
            {profile.bio && (
              <Typography variant="body2" mt={0.5} color="text.secondary">
                {profile.bio}
              </Typography>
            )}
          </Box>
          {isOwn && (
            <Button
              startIcon={<EditIcon />}
              variant="outlined"
              size="small"
              onClick={() => setEditOpen(true)}
            >
              {t("profile.editProfile")}
            </Button>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" fontWeight={700}>{userPosts.length}</Typography>
            <Typography variant="caption" color="text.secondary">{t("common.posts")}</Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" fontWeight={700}>{userCompanies.length}</Typography>
            <Typography variant="caption" color="text.secondary">{t("common.business")}</Typography>
          </Box>
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tab label={t("common.posts")} />
          <Tab label={t("common.business")} />
          <Tab label={t("common.about")} />
        </Tabs>

        <TabPanel value={tab} index={0}>
          {userPosts.length === 0 ? (
            <Typography color="text.secondary" py={4} textAlign="center">{t("profile.noPosts")}</Typography>
          ) : (
            userPosts.map((post) =>
              isOwn ? (
                <Card key={post.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(post.created_at).toLocaleDateString()} ·{" "}
                      <Chip
                        label={t(POST_TYPE_KEYS[post.post_type] ?? "feed.other")}
                        size="small"
                        sx={{ fontSize: "0.65rem", height: 18 }}
                      />
                      {post.is_anonymous && (
                        <Chip
                          label={t("profile.postedAnonymously")}
                          size="small"
                          sx={{ fontSize: "0.65rem", height: 18, ml: 0.5 }}
                        />
                      )}
                    </Typography>
                    <Typography variant="body2" mt={0.5}>
                      {post.content}
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <PostCard key={post.id} post={post} onRefresh={loadProfile} />
              ),
            )
          )}
        </TabPanel>

        <TabPanel value={tab} index={1}>
          {userCompanies.length === 0 ? (
            <Typography color="text.secondary" py={4} textAlign="center">{t("profile.noCompanies")}</Typography>
          ) : (
            userCompanies.map((co) => (
              <Card key={co.id} sx={{ mb: 2 }}>
                <CardContent sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                  <Avatar sx={{ bgcolor: "secondary.main", width: 40, height: 40 }}>
                    <BusinessIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{co.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{co.company_type}</Typography>
                    <Typography variant="body2" mt={0.5}>{co.description}</Typography>
                  </Box>
                </CardContent>
                {!isOwn && currentUser && (
                  <CardActions sx={{ px: 2, pt: 0, pb: 1.5 }}>
                    <Button
                      size="small"
                      startIcon={<MessageIcon fontSize="small" />}
                      sx={{ ml: "auto" }}
                      onClick={() => {
                        setInquireError(null);
                        setInquireCompany(co);
                        setInquireOpen(true);
                      }}
                    >
                      {t("business.inquire")}
                    </Button>
                  </CardActions>
                )}
              </Card>
            ))
          )}
        </TabPanel>

        <TabPanel value={tab} index={2}>
          <Card>
            <CardContent>
              {isOwn && profile.email && (
                <Typography variant="body2"><strong>{t("profile.emailLabel")}:</strong> {profile.email}</Typography>
              )}
              {profile.bio && (
                <Typography variant="body2" mt={1}><strong>{t("common.bio")}:</strong> {profile.bio}</Typography>
              )}
              <Typography variant="body2" mt={1}>
                <strong>{t("common.joinDate")}:</strong>{" "}
                {new Date(profile.created_at).toLocaleDateString()}
              </Typography>
            </CardContent>
          </Card>
        </TabPanel>
      </Container>

      {/* Edit Profile Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("profile.editProfile")}</DialogTitle>
        <DialogContent>
          <TextField
            label={t("auth.firstName")}
            fullWidth
            margin="normal"
            value={editForm.first_name}
            onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))}
          />
          <TextField
            label={t("auth.lastName")}
            fullWidth
            margin="normal"
            value={editForm.last_name}
            onChange={(e) => setEditForm((f) => ({ ...f, last_name: e.target.value }))}
          />
          <TextField
            label={t("common.bio")}
            fullWidth
            margin="normal"
            multiline
            rows={3}
            value={editForm.bio}
            onChange={(e) => setEditForm((f) => ({ ...f, bio: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editLoading}>
            {editLoading ? t("common.saving") : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inquire Dialog (Business tab) */}
      <Dialog open={inquireOpen} onClose={() => setInquireOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("business.sendInquiry")} — {inquireCompany?.name}</DialogTitle>
        <DialogContent>
          {inquireError && (
            <Alert severity="error" sx={{ mt: 1 }}>{inquireError}</Alert>
          )}
          <TextField
            label={t("business.inquiryMessage")}
            fullWidth
            margin="normal"
            multiline
            rows={4}
            value={inquireMsg}
            onChange={(e) => setInquireMsg(e.target.value)}
            placeholder={t("business.inquiryPlaceholder")}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInquireOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleInquire}
            disabled={inquireLoading || !inquireMsg.trim()}
            sx={{ color: "white", "&.Mui-disabled": { color: "rgba(255,255,255,0.7)", bgcolor: "rgba(0,0,0,0.25)" } }}
          >
            {inquireLoading ? t("common.sending") : t("common.send")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
